import { formatDuration } from '@cfl/shared';

import {
  Card, CardContent, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { ClockArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { useMySubmissions } from '@/lib/approvals-api';
import { WeekBadge } from '@/routes/overview';

/** Your own weeks and what happened to them. The API already returns the last 26. */
export function MySubmissionsPage() {
  const { data, isPending } = useMySubmissions();

  return (
    <PageContainer>
      <PageHeader title="My submissions" description="Weeks you have sent for approval." />

      {isPending && <Skeleton className="h-72 w-full" />}

      {data && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[760px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Week</TableHead>
                    <TableHead className="text-right">Recorded</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Decided</TableHead>
                    <TableHead>What they said</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((week) => (
                    <TableRow key={week.id}>
                      <TableCell className="ts-num whitespace-nowrap">
                        {range(week.periodStart, week.periodEnd)}
                      </TableCell>
                      <TableCell className="ts-num text-right">
                        {formatDuration(week.actualSeconds ?? 0)}
                      </TableCell>
                      <TableCell className="ts-num text-right text-muted-foreground">
                        {/* 1,356 migrated weeks carry zero because the old system never
                            stored it; "0:00" would read as "you owed nothing". */}
                        {week.expectedSeconds > 0 ? formatDuration(week.expectedSeconds) : '—'}
                      </TableCell>
                      <TableCell><WeekBadge status={week.status} /></TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {week.decidedAt ? day(week.decidedAt) : '—'}
                      </TableCell>
                      {/* The reason, which until now lived only in an email. Wrapped
                          rather than truncated: a sentence explaining what to fix is
                          no use with its end cut off. */}
                      <TableCell className="max-w-[22rem] text-sm text-muted-foreground">
                        {week.decision?.message ? (
                          <span className="whitespace-pre-wrap">{week.decision.message}</span>
                        ) : week.decision?.by ? (
                          <span className="opacity-60">by {week.decision.by}</span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <EmptyState art={ClockArt} title="You have not submitted a week yet" body="Record your time on My week, then submit it — every submission and its decision shows up here." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

const UTC = { day: 'numeric', month: 'short', timeZone: 'UTC' } as const;

function range(from: string, to: string): string {
  const a = new Date(`${from}T00:00:00.000Z`);
  const b = new Date(`${to}T00:00:00.000Z`);
  const left = a.getUTCMonth() === b.getUTCMonth() ? a.getUTCDate() : a.toLocaleDateString('en-IN', UTC);
  return `${left} – ${b.toLocaleDateString('en-IN', { ...UTC, year: 'numeric' })}`;
}

const day = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { ...UTC, year: 'numeric' });
